import { Injectable } from '@angular/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import {
    RESEARCH_ACCESS,
    RESEARCH_CAREER_JOB_PROFILES,
    RESEARCH_CAREER_MATCHES,
    RESEARCH_CAREER_NARRATIVE,
    RESEARCH_CV_ASSERT_TAG,
    RESEARCH_CV_REVIEW_TAGS,
    RESEARCH_DOCUMENT_DOWNLOAD_URL,
    RESEARCH_MY_CV,
    RESEARCH_DOCUMENT_DELETE,
    RESEARCH_DOCUMENT_FINALIZE,
    RESEARCH_DOCUMENT_UPLOAD_URL,
    RESEARCH_GRANT_DETAIL,
    RESEARCH_PINNED_GRANTS,
    RESEARCH_GRANT_FACETS,
    RESEARCH_GRANT_SEARCH,
    RESEARCH_HOME,
    RESEARCH_JOB_DETAIL,
    RESEARCH_JOB_FACETS,
    RESEARCH_JOB_PORTAL_SEARCH,
    RESEARCH_MY_PROFILE,
    RESEARCH_PEOPLE_FACETS,
    RESEARCH_PEOPLE_SEARCH,
    RESEARCH_PERSON_PROFILE,
    RESEARCH_PINNED_PEOPLE,
    RESEARCH_PORTAL_CONFIG,
    RESEARCH_RECOMMENDATIONS,
    RESEARCH_RECORD_VISIT,
    RESEARCH_SAVED_ITEMS,
    RESEARCH_SEARCH_ALL,
    RESEARCH_SET_ITEM_STATE,
} from '../graphql/research-operations';
import {
    ItemKind,
    ResearchAccess,
    ResearchCareerMatches,
    ResearchCareerNarrative,
    ResearchCvReviewStatus,
    ResearchJobProfile,
    ResearchMemberCv,
    ResearchDocumentUploadTarget,
    ResearchGrantFacets,
    ResearchGrantMatch,
    ResearchGrantSearchInput,
    ResearchGrantSearchResult,
    ResearchHome,
    ResearchItemState,
    ResearchJobDetail,
    ResearchJobFacets,
    ResearchJobPortalSearchInput,
    ResearchJobSearchResult,
    ResearchMemberDocument,
    ResearchPeopleFacets,
    ResearchPeopleSearchInput,
    ResearchPeopleSearchResult,
    ResearchPersonMatch,
    ResearchPersonProfile,
    ResearchPortalConfig,
    ResearchRecommendations,
    ResearchSavedItems,
    ResearchSearchAll,
} from '../models/research.models';
import { AGURenderService } from 'agu_app_shell';

/** A document upload purpose: the member's CV, or a one-off search attachment. */
export type ResearchDocumentPurpose = 'cv' | 'search';

/** MIME types the portal accepts for CV/search-attachment uploads — same table as packages/MAPSUI/src/lib/services/maps-upload.service.ts. */
const DOCUMENT_CONTENT_TYPES: Readonly<Record<string, string>> = {
    txt: 'text/plain',
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

/** Maps an uploaded file's extension to the MIME type the upload target expects, or null when unsupported. */
export function InferDocumentContentType(file: File): string | null {
    const ix = file.name.lastIndexOf('.');
    if (ix === -1) return null;
    const ext = file.name.slice(ix + 1).toLowerCase();
    return DOCUMENT_CONTENT_TYPES[ext] ?? null;
}

/** The browser's IANA zone (e.g. `America/Los_Angeles`): Home's streak and month count read the member's local days (L128). Null when unknown; the server then reads Eastern. */
export function BrowserTimeZone(): string | null {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
}

/**
 * Typed transport for the Research Accelerator / AGU Member Hub GraphQL
 * surface. Every method follows the same shape: call `GraphQLDataProvider
 * .ExecuteGQL` (MJ's own `/graphql`, not MAPS's apollo-angular client — see
 * ra-surface.module.ts), unwrap the one field the operation returns, and throw
 * on a missing/null result rather than handing callers an `undefined`.
 *
 * Provided by RaSurfaceComponent (its element injector, not RaSurfaceModule or
 * `providedIn: 'root'` — both of those are the one root injector) so two
 * Research Accelerator tabs open at once each get their own instance — same
 * choice as RaCompositionState.
 *
 * THIS IS ALSO THE SURFACE'S REPAINT BOUNDARY. MJExplorer runs zoneless
 * (`NgZone` is `NoopNgZone` — see AGURenderService's header for the framework
 * trace), so assigning `this.loading = false` after an `await` schedules no
 * change detection at all. Every operation below therefore goes through
 * `Execute`, which asks `AGURenderService` for a coalesced repaint the moment
 * it settles — success or failure. That is one place instead of a
 * `detectChanges()` after every await in twenty components, and it cannot be
 * forgotten by a new page: if a load goes through this service, it paints.
 */
@Injectable()
export class ResearchApiService {
    constructor(private readonly render: AGURenderService) {}

    /**
     * The one call into MJ's GraphQL transport, and the one place the surface
     * is told to repaint.
     *
     * `GraphQLDataProvider.ExecuteGQL` is declared as `Promise<any>`; the cast
     * through `unknown` is the single, contained place that untyped boundary is
     * given a shape, and every caller supplies the operation's own result type
     * contextually (`const result: { researchHome: ResearchHome } | null = await
     * this.Execute(...)`), exactly as they did against the raw provider.
     */
    private async Execute<TResult>(document: string, variables: Record<string, unknown>, refreshToken: boolean): Promise<TResult> {
        try {
            return (await GraphQLDataProvider.ExecuteGQL(document, variables, refreshToken)) as unknown as TResult;
        } finally {
            this.render.Notify();
        }
    }

    // ── Access / config ─────────────────────────────────────────────────
    async Access(): Promise<ResearchAccess> {
        const result: { researchAccess: ResearchAccess } | null = await this.Execute(RESEARCH_ACCESS, {}, true);
        if (!result?.researchAccess) throw new Error('researchAccess returned no data');
        return result.researchAccess;
    }

    async PortalConfig(): Promise<ResearchPortalConfig> {
        const result: { researchPortalConfig: ResearchPortalConfig } | null = await this.Execute(RESEARCH_PORTAL_CONFIG, {}, true);
        if (!result?.researchPortalConfig) throw new Error('researchPortalConfig returned no data');
        return result.researchPortalConfig;
    }

    // ── Grant Finder ────────────────────────────────────────────────────
    async SearchGrants(input: ResearchGrantSearchInput): Promise<ResearchGrantSearchResult> {
        const result: { researchGrantSearch: ResearchGrantSearchResult } | null = await this.Execute(RESEARCH_GRANT_SEARCH, { input }, true);
        if (!result?.researchGrantSearch) throw new Error('researchGrantSearch returned no data');
        return result.researchGrantSearch;
    }

    async GrantFacets(): Promise<ResearchGrantFacets> {
        const result: { researchGrantFacets: ResearchGrantFacets } | null = await this.Execute(RESEARCH_GRANT_FACETS, {}, true);
        if (!result?.researchGrantFacets) throw new Error('researchGrantFacets returned no data');
        return result.researchGrantFacets;
    }

    async GrantDetail(contentID: string): Promise<ResearchGrantMatch | null> {
        const result: { researchGrantDetail: ResearchGrantMatch | null } | null = await this.Execute(RESEARCH_GRANT_DETAIL, { contentID }, true);
        if (result === null || result === undefined) throw new Error('researchGrantDetail returned no data');
        return result.researchGrantDetail;
    }

    async PinnedGrants(): Promise<ResearchGrantMatch[]> {
        const result: { researchPinnedGrants: ResearchGrantMatch[] } | null = await this.Execute(RESEARCH_PINNED_GRANTS, {}, true);
        if (!result?.researchPinnedGrants) throw new Error('researchPinnedGrants returned no data');
        return result.researchPinnedGrants;
    }

    // ── Collaborator Finder ─────────────────────────────────────────────
    async SearchPeople(input: ResearchPeopleSearchInput): Promise<ResearchPeopleSearchResult> {
        const result: { researchPeopleSearch: ResearchPeopleSearchResult } | null = await this.Execute(RESEARCH_PEOPLE_SEARCH, { input }, true);
        if (!result?.researchPeopleSearch) throw new Error('researchPeopleSearch returned no data');
        return result.researchPeopleSearch;
    }

    async PeopleFacets(): Promise<ResearchPeopleFacets> {
        const result: { researchPeopleFacets: ResearchPeopleFacets } | null = await this.Execute(RESEARCH_PEOPLE_FACETS, {}, true);
        if (!result?.researchPeopleFacets) throw new Error('researchPeopleFacets returned no data');
        return result.researchPeopleFacets;
    }

    async PersonProfile(contributorID: string): Promise<ResearchPersonProfile | null> {
        const result: { researchPersonProfile: ResearchPersonProfile | null } | null = await this.Execute(RESEARCH_PERSON_PROFILE, { contributorID }, true);
        if (result === null || result === undefined) throw new Error('researchPersonProfile returned no data');
        return result.researchPersonProfile;
    }

    async PinnedPeople(): Promise<ResearchPersonMatch[]> {
        const result: { researchPinnedPeople: ResearchPersonMatch[] } | null = await this.Execute(RESEARCH_PINNED_PEOPLE, {}, true);
        if (!result?.researchPinnedPeople) throw new Error('researchPinnedPeople returned no data');
        return result.researchPinnedPeople;
    }

    // ── Job Finder ──────────────────────────────────────────────────────
    async SearchJobs(input: ResearchJobPortalSearchInput): Promise<ResearchJobSearchResult> {
        const result: { researchJobPortalSearch: ResearchJobSearchResult } | null = await this.Execute(RESEARCH_JOB_PORTAL_SEARCH, { input }, true);
        if (!result?.researchJobPortalSearch) throw new Error('researchJobPortalSearch returned no data');
        return result.researchJobPortalSearch;
    }

    async JobFacets(): Promise<ResearchJobFacets> {
        const result: { researchJobFacets: ResearchJobFacets } | null = await this.Execute(RESEARCH_JOB_FACETS, {}, true);
        if (!result?.researchJobFacets) throw new Error('researchJobFacets returned no data');
        return result.researchJobFacets;
    }

    async JobDetail(jobID: string): Promise<ResearchJobDetail | null> {
        const result: { researchJobDetail: ResearchJobDetail | null } | null = await this.Execute(RESEARCH_JOB_DETAIL, { jobID }, true);
        if (result === null || result === undefined) throw new Error('researchJobDetail returned no data');
        return result.researchJobDetail;
    }

    // ── Search everything / recommendations / home ─────────────────────
    async SearchAll(query: string, perKind: number): Promise<ResearchSearchAll> {
        const result: { researchSearchAll: ResearchSearchAll } | null = await this.Execute(RESEARCH_SEARCH_ALL, { query, perKind }, true);
        if (!result?.researchSearchAll) throw new Error('researchSearchAll returned no data');
        return result.researchSearchAll;
    }

    /** `includeCoauthors`: people only; the server hides the member's co-authors unless true. */
    async Recommendations(kinds: ItemKind[], count: number, includeCoauthors = false): Promise<ResearchRecommendations> {
        const result: { researchRecommendations: ResearchRecommendations } | null = await this.Execute(RESEARCH_RECOMMENDATIONS, { kinds, count, includeCoauthors }, true);
        if (!result?.researchRecommendations) throw new Error('researchRecommendations returned no data');
        return result.researchRecommendations;
    }

    async Home(): Promise<ResearchHome> {
        const result: { researchHome: ResearchHome } | null = await this.Execute(RESEARCH_HOME, { timeZone: BrowserTimeZone() }, true);
        if (!result?.researchHome) throw new Error('researchHome returned no data');
        return result.researchHome;
    }

    async RecordVisit(): Promise<ResearchHome> {
        const result: { researchRecordVisit: ResearchHome } | null = await this.Execute(RESEARCH_RECORD_VISIT, { timeZone: BrowserTimeZone() }, true);
        if (!result?.researchRecordVisit) throw new Error('researchRecordVisit returned no data');
        return result.researchRecordVisit;
    }

    async SavedItems(kind: ItemKind | null): Promise<ResearchSavedItems> {
        const result: { researchSavedItems: ResearchSavedItems } | null = await this.Execute(RESEARCH_SAVED_ITEMS, { kind }, true);
        if (!result?.researchSavedItems) throw new Error('researchSavedItems returned no data');
        return result.researchSavedItems;
    }

    // ── Profile ─────────────────────────────────────────────────────────
    /**
     * The signed-in member's own profile, fetched at most ONCE per surface
     * session.
     *
     * `researchMyProfile` is an aggregate — works, funding, sessions, peer
     * review, stats — and five callers want it (the sidebar's role line, the
     * profile page's `IsSelf` path, Settings, Career Map, Job Finder). Opening
     * the portal and then the profile page used to pay for that aggregate
     * twice within a second for a value that cannot change in between.
     *
     * The memo is the PROMISE, not the result, so two callers racing on open
     * share the single in-flight request rather than starting two. A rejection
     * clears it (see `FetchMyProfile`), so a failed load is never cached and
     * the next caller genuinely retries; `ClearSessionCache` clears it when the
     * surface is torn down or the member signs out, and the two document
     * mutations clear it because they are the only operations that change what
     * this aggregate would return (`currentCV`).
     */
    private myProfilePromise: Promise<ResearchPersonProfile> | null = null;

    async MyProfile(): Promise<ResearchPersonProfile> {
        const pending = this.myProfilePromise ?? this.FetchMyProfile();
        this.myProfilePromise = pending;
        try {
            return await pending;
        } finally {
            // A memo HIT short-circuits `Execute`, so it would otherwise never
            // repaint the caller on this zoneless host (see AGURenderService) —
            // a page whose only load is MyProfile() would render its spinner and
            // freeze. PortalConfigService.Load() carries the same guard for the
            // same reason.
            this.render.Notify();
        }
    }

    private async FetchMyProfile(): Promise<ResearchPersonProfile> {
        try {
            const result: { researchMyProfile: ResearchPersonProfile } | null = await this.Execute(RESEARCH_MY_PROFILE, {}, true);
            if (!result?.researchMyProfile) throw new Error('researchMyProfile returned no data');
            return result.researchMyProfile;
        } catch (error) {
            // Never remember a failure: a transport blip must not lock the
            // member out of their own profile for the rest of the session.
            this.myProfilePromise = null;
            throw error;
        }
    }

    /**
     * Drop everything memoised for this session. Called by the surface on
     * destroy and on sign-out, so a second member signing in on the same tab
     * can never read the first one's profile.
     */
    ClearSessionCache(): void {
        this.myProfilePromise = null;
    }

    // ── Item state ──────────────────────────────────────────────────────
    async SetItemState(kind: ItemKind, key: string, patch: { saved?: boolean; pinned?: boolean; dismissed?: boolean }): Promise<ResearchItemState> {
        const result: { researchSetItemState: ResearchItemState } | null = await this.Execute(
            RESEARCH_SET_ITEM_STATE,
            { kind, key, saved: patch.saved, pinned: patch.pinned, dismissed: patch.dismissed },
            true
        );
        if (!result?.researchSetItemState) throw new Error('researchSetItemState returned no data');
        return result.researchSetItemState;
    }

    // ── Documents ───────────────────────────────────────────────────────
    async DocumentUploadUrl(file: File, purpose: ResearchDocumentPurpose, consent: boolean): Promise<ResearchDocumentUploadTarget> {
        const contentType = InferDocumentContentType(file);
        if (!contentType) throw new Error('Only PDF, DOCX or TXT files can be attached.');
        const result: { researchDocumentUploadUrl: ResearchDocumentUploadTarget } | null = await this.Execute(
            RESEARCH_DOCUMENT_UPLOAD_URL,
            { fileName: file.name, contentType, sizeBytes: file.size, purpose, consent },
            true
        );
        if (!result?.researchDocumentUploadUrl) throw new Error('researchDocumentUploadUrl returned no data');
        return result.researchDocumentUploadUrl;
    }

    /**
     * Clears the `MyProfile` memo: a finalized CV changes `currentCV` on that
     * aggregate. Over-invalidates for search attachments, which costs one
     * refetch and cannot be wrong.
     *
     * Cleared on BOTH sides of the await: clearing only before it leaves a
     * window in which a `MyProfile()` racing the in-flight mutation re-memoises
     * the pre-mutation aggregate for the rest of the session. The second clear
     * costs nothing and closes it.
     */
    async DocumentFinalize(documentID: string): Promise<ResearchMemberDocument> {
        this.ClearSessionCache();
        const result: { researchDocumentFinalize: ResearchMemberDocument } | null = await this.Execute(RESEARCH_DOCUMENT_FINALIZE, { documentID }, true);
        this.ClearSessionCache();
        if (!result?.researchDocumentFinalize) throw new Error('researchDocumentFinalize returned no data');
        return result.researchDocumentFinalize;
    }

    /** Clears the `MyProfile` memo, on both sides of the await, for the same reasons `DocumentFinalize` does. */
    async DocumentDelete(documentID: string): Promise<boolean> {
        this.ClearSessionCache();
        const result: { researchDocumentDelete: boolean } | null = await this.Execute(RESEARCH_DOCUMENT_DELETE, { documentID }, true);
        this.ClearSessionCache();
        if (result === null || result === undefined) throw new Error('researchDocumentDelete returned no data');
        return result.researchDocumentDelete;
    }

    /**
     * The three-step composite upload (TRD pattern shared with
     * packages/MAPSUI/src/lib/services/maps-upload.service.ts): mint a SAS
     * write URL → PUT the file straight to Azure Blob → finalize server-side
     * (text extraction). A CV upload carries the member's `consent`; the server
     * refuses a CV without it.
     */
    async UploadDocument(file: File, purpose: ResearchDocumentPurpose, consent: boolean): Promise<ResearchMemberDocument> {
        const contentType = InferDocumentContentType(file);
        if (!contentType) throw new Error('Only PDF, DOCX or TXT files can be attached.');
        const target = await this.DocumentUploadUrl(file, purpose, consent);
        try {
            const put = await fetch(target.uploadUrl, {
                method: 'PUT',
                headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType },
                body: file,
            });
            if (!put.ok) throw new Error(`Upload failed (${put.status})`);
            return await this.DocumentFinalize(target.documentID);
        } finally {
            // The blob PUT is the one leg of this composite that is not a
            // GraphQL call, so it is the one leg `Execute` cannot repaint for.
            // A failed upload has an error state to show like any other.
            this.render.Notify();
        }
    }

    /** A short-lived download URL for one of the member's own documents. */
    async DocumentDownloadUrl(documentID: string): Promise<string> {
        const result: { researchDocumentDownloadUrl: string } | null = await this.Execute(RESEARCH_DOCUMENT_DOWNLOAD_URL, { documentID }, true);
        if (!result?.researchDocumentDownloadUrl) throw new Error('researchDocumentDownloadUrl returned no data');
        return result.researchDocumentDownloadUrl;
    }

    // ── CV and career explorer ──────────────────────────────────────────
    /** The member's current CV, or null when they have none. */
    async MyCv(): Promise<ResearchMemberCv | null> {
        const result: { researchMyCv: ResearchMemberCv | null } | null = await this.Execute(RESEARCH_MY_CV, {}, true);
        if (result === null || result === undefined) throw new Error('researchMyCv returned no data');
        return result.researchMyCv;
    }

    /** Sets the review status of CV tags and returns how many changed; clears the session cache, which a review can change. */
    async ReviewTags(providerTagIDs: string[], status: ResearchCvReviewStatus): Promise<number> {
        this.ClearSessionCache();
        const result: { researchCvReviewTags: number } | null = await this.Execute(RESEARCH_CV_REVIEW_TAGS, { providerTagIDs, status }, true);
        this.ClearSessionCache();
        if (result === null || result === undefined) throw new Error('researchCvReviewTags returned no data');
        return result.researchCvReviewTags;
    }

    /** The roles the member's CV matches; `refresh` re-reads them from the CV provider. */
    async CareerMatches(refresh: boolean): Promise<ResearchCareerMatches> {
        const result: { researchCareerMatches: ResearchCareerMatches } | null = await this.Execute(RESEARCH_CAREER_MATCHES, { refresh }, true);
        if (!result?.researchCareerMatches) throw new Error('researchCareerMatches returned no data');
        return result.researchCareerMatches;
    }

    /** What each role looks like across postings; the server accepts at most 20 role refs. */
    async CareerJobProfiles(roleRefs: string[]): Promise<ResearchJobProfile[]> {
        const result: { researchCareerJobProfiles: ResearchJobProfile[] } | null = await this.Execute(RESEARCH_CAREER_JOB_PROFILES, { roleRefs }, true);
        if (!result?.researchCareerJobProfiles) throw new Error('researchCareerJobProfiles returned no data');
        return result.researchCareerJobProfiles;
    }

    /** Why a role fits the member; generated by the CV provider on first request, then stored. */
    async CareerNarrative(roleRef: string): Promise<ResearchCareerNarrative> {
        const result: { researchCareerNarrative: ResearchCareerNarrative } | null = await this.Execute(RESEARCH_CAREER_NARRATIVE, { roleRef }, true);
        if (!result?.researchCareerNarrative) throw new Error('researchCareerNarrative returned no data');
        return result.researchCareerNarrative;
    }

    /** The member confirms (or withdraws) a requirement their CV does not evidence; clears the session cache. */
    async AssertTag(providerTagID: string, asserted: boolean): Promise<boolean> {
        this.ClearSessionCache();
        const result: { researchCvAssertTag: boolean } | null = await this.Execute(RESEARCH_CV_ASSERT_TAG, { providerTagID, asserted }, true);
        this.ClearSessionCache();
        if (result === null || result === undefined) throw new Error('researchCvAssertTag returned no data');
        return result.researchCvAssertTag;
    }
}
